// Builds CSV files in the layout QuickBooks Online's own importers expect
// (Settings > Import data > Products and services / Customers / Invoices).
//
// QuickBooks Online rules this follows:
// - Invoices: one row per line item, each repeating InvoiceNo, Customer,
//   InvoiceDate and DueDate. Required: InvoiceNo, Customer, InvoiceDate,
//   DueDate, ItemAmount. Taxable is Y or N. At most 100 invoices and
//   1,000 rows per file. Invoices can't have a zero or negative total.
// - Customer and product names must match exactly between files, must be
//   unique, and ":" means "sub-customer" / "sub-item", so it's replaced.
// - Dates as MM/DD/YYYY (choose that format on the import screen).
// - Invoice numbers are kept only with Custom transaction numbers turned
//   on in QuickBooks (Settings > Sales > Sales form content).
// Everything here is pure so it can be unit-tested.

export type QbCustomer = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zip: string;
  createdAt?: number;
};

export type QbProduct = {
  id: string;
  title: string;
  description: string;
  price: number;
  taxable: boolean;
};

export type QbLine = {
  productId: string;
  productName: string;
  productNotes: string;
  quantity: number;
  unitPrice: number;
};

export type QbProposal = {
  id: string;
  customerId: string;
  firstName: string;
  lastName: string;
  status: string;
  invoiceDate?: number;
  invoiceDueDate?: number;
  createdAt: number;
  taxRate: number;
  taxAmount: number;
  totalAmount: number;
  archivedAt?: number | null;
  serviceAddressLine1: string;
  serviceCity: string;
  serviceState: string;
  serviceZip: string;
  lines: QbLine[];
};

export type Csv = string[][];

export type InvoiceProblem = { id: string; customer: string; message: string };

export type InvoiceExport = {
  files: Csv[]; // each starts with the header row
  invoiceCount: number;
  rowCount: number;
  total: number; // before tax, as QuickBooks will see it
  tax: number; // as the CRM calculated it; QuickBooks recalculates
  skipped: InvoiceProblem[];
  warnings: InvoiceProblem[];
};

export const MAX_INVOICES_PER_FILE = 100;
export const MAX_ROWS_PER_FILE = 999; // plus the header row = 1,000

export const INVOICE_HEADER = [
  "InvoiceNo",
  "Customer",
  "InvoiceDate",
  "DueDate",
  "Terms",
  "Location",
  "Memo",
  "Item(Product/Service)",
  "ItemDescription",
  "ItemQuantity",
  "ItemRate",
  "ItemAmount",
  "Taxable",
  "TaxRate",
];

export const CUSTOMER_HEADER = [
  "Name",
  "Email",
  "Phone",
  "Street",
  "City",
  "State",
  "ZIP",
  "Country",
];

export const PRODUCT_HEADER = [
  "Product/Service Name",
  "Sales Description",
  "Type",
  "Sales Price / Rate",
  "Taxable",
  "Income Account",
];

const DAY = 86400000;

// Clean a name for QuickBooks: no colons (sub-customer/sub-item marker),
// no line breaks or control characters, single spaces, length limit.
export function qbName(s: string | undefined, max = 100): string {
  return (s || "")
    .replace(/[\u0000-\u001f\u007f]+/g, " ")
    .replace(/:/g, " -")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, max)
    .trim();
}

// Free text (descriptions, memo): one line, length limit.
export function qbText(s: string | undefined, max = 4000): string {
  return (s || "")
    .replace(/\r?\n+/g, " ")
    .replace(/[\u0000-\u001f\u007f]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, max);
}

export function qbDate(ms: number): string {
  const d = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(d.getMonth() + 1)}/${pad(d.getDate())}/${d.getFullYear()}`;
}

export const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

// Plain numbers: no currency signs or thousands separators.
export function qbNumber(n: number, maxDecimals = 2): string {
  const v = Number.isFinite(n) ? n : 0;
  return String(Number(v.toFixed(maxDecimals)));
}

// One unique QuickBooks display name per customer. People with the same
// name get their street (or city) added, so both files agree on who's who.
export function customerNames(customers: QbCustomer[]): Map<string, string> {
  const base = (c: QbCustomer) =>
    qbName(`${c.firstName || ""} ${c.lastName || ""}`, 90) || "Unnamed customer";
  const groups = new Map<string, QbCustomer[]>();
  for (const c of customers) {
    const key = base(c).toLowerCase();
    groups.set(key, [...(groups.get(key) || []), c]);
  }
  const names = new Map<string, string>();
  const used = new Set<string>();
  const take = (id: string, name: string) => {
    let n = name;
    for (let i = 2; used.has(n.toLowerCase()); i++) n = `${name} ${i}`;
    used.add(n.toLowerCase());
    names.set(id, n);
  };
  // Unique names first, so a plain "John Smith" never gets renamed.
  Array.from(groups.values())
    .filter((g) => g.length === 1)
    .forEach((g) => take(g[0].id, base(g[0])));
  Array.from(groups.values())
    .filter((g) => g.length > 1)
    .forEach((g) =>
      [...g]
        .sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0) || a.id.localeCompare(b.id))
        .forEach((c) => {
          const where = qbName(c.addressLine1 || c.city, 40) || c.id.slice(0, 6);
          take(c.id, `${base(c)} (${where})`);
        })
    );
  return names;
}

export function productNames(products: QbProduct[]): Map<string, string> {
  const names = new Map<string, string>();
  const used = new Set<string>();
  [...products]
    .sort((a, b) => a.id.localeCompare(b.id))
    .forEach((p) => {
      const name = qbName(p.title, 95) || "Unnamed service";
      let n = name;
      for (let i = 2; used.has(n.toLowerCase()); i++) n = `${name} (${i})`;
      used.add(n.toLowerCase());
      names.set(p.id, n);
    });
  return names;
}

// Split rows into files under QuickBooks' per-file row limit.
function chunkRows(header: string[], rows: Csv): Csv[] {
  const files: Csv[] = [];
  for (let i = 0; i < rows.length; i += MAX_ROWS_PER_FILE) {
    files.push([header, ...rows.slice(i, i + MAX_ROWS_PER_FILE)]);
  }
  return files.length ? files : [[header]];
}

export function buildCustomerFiles(customers: QbCustomer[]): Csv[] {
  const names = customerNames(customers);
  const rows = [...customers]
    .sort((a, b) => (names.get(a.id) || "").localeCompare(names.get(b.id) || ""))
    .map((c) => [
      names.get(c.id) || "",
      (c.email || "").trim(),
      (c.phone || "").trim(),
      qbText([c.addressLine1, c.addressLine2].filter((s) => s && s.trim()).join(", "), 500),
      qbText(c.city, 255),
      qbText(c.state, 255),
      qbText(c.zip, 30),
      "United States",
    ]);
  return chunkRows(CUSTOMER_HEADER, rows);
}

export function buildProductFiles(products: QbProduct[], incomeAccount: string): Csv[] {
  const names = productNames(products);
  const rows = [...products]
    .sort((a, b) => (names.get(a.id) || "").localeCompare(names.get(b.id) || ""))
    .map((p) => [
      names.get(p.id) || "",
      qbText(p.description),
      "Service",
      qbNumber(p.price || 0, 4),
      p.taxable ? "Yes" : "No",
      qbName(incomeAccount, 100),
    ]);
  return chunkRows(PRODUCT_HEADER, rows);
}

export function buildInvoiceFiles(
  proposals: QbProposal[],
  customers: QbCustomer[],
  products: QbProduct[],
  options: { from?: number; to?: number; includeArchived?: boolean } = {}
): InvoiceExport {
  const custNames = customerNames(customers);
  const prodNames = productNames(products);
  const productById = new Map(products.map((p) => [p.id, p]));
  const skipped: InvoiceProblem[] = [];
  const warnings: InvoiceProblem[] = [];

  const dateOf = (p: QbProposal) => Number(p.invoiceDate) || Number(p.createdAt) || 0;
  const from = options.from ?? -Infinity;
  // "To" is a whole day: include everything up to the end of it.
  const to = options.to !== undefined ? options.to + DAY : Infinity;

  const chosen = proposals
    .filter((p) => p.status === "WON" || p.status === "DONE")
    .filter((p) => options.includeArchived !== false || !p.archivedAt)
    .filter((p) => dateOf(p) >= from && dateOf(p) < to)
    .sort((a, b) => dateOf(a) - dateOf(b) || a.id.localeCompare(b.id));

  const invoices: Csv[] = [];
  let total = 0;
  let tax = 0;
  for (const p of chosen) {
    const customer =
      custNames.get(p.customerId) ||
      qbName(`${p.firstName || ""} ${p.lastName || ""}`, 90) ||
      "Unnamed customer";
    const problem = (message: string) => ({ id: p.id, customer, message });

    if (!p.lines.length) {
      skipped.push(problem("has no line items"));
      continue;
    }
    if (!custNames.has(p.customerId)) {
      warnings.push(
        problem("its customer was deleted from the CRM; QuickBooks needs a customer with this exact name")
      );
    }
    const date = dateOf(p);
    const due = Math.max(Number(p.invoiceDueDate) || 0, date);
    const memo = qbText(
      [
        p.serviceAddressLine1 &&
          `Service address: ${[p.serviceAddressLine1, p.serviceCity, `${p.serviceState || ""} ${p.serviceZip || ""}`.trim()]
            .filter(Boolean)
            .join(", ")}`,
        `CRM proposal ${p.id}`,
      ]
        .filter(Boolean)
        .join(". "),
      1000
    );

    let invoiceTotal = 0;
    const rows: Csv = p.lines.map((l) => {
      const product = productById.get(l.productId);
      if (!product) {
        warnings.push(
          problem(`uses "${qbName(l.productName)}", which is no longer in the product list`)
        );
      }
      const amount = round2((Number(l.quantity) || 0) * (Number(l.unitPrice) || 0));
      invoiceTotal += amount;
      const taxable = product
        ? !!product.taxable
        : (Number(p.taxAmount) || 0) > 0; // best guess for a deleted product
      return [
        p.id,
        customer,
        qbDate(date),
        qbDate(due),
        "",
        "",
        memo,
        (product && prodNames.get(product.id)) || qbName(l.productName) || "Services",
        qbText(l.productNotes),
        qbNumber(Number(l.quantity) || 0, 4),
        qbNumber(Number(l.unitPrice) || 0, 4),
        qbNumber(amount),
        taxable ? "Y" : "N",
        taxable && Number(p.taxRate) > 0 ? `${qbNumber(Number(p.taxRate), 4)}%` : "",
      ];
    });
    invoiceTotal = round2(invoiceTotal);
    if (invoiceTotal <= 0) {
      skipped.push(problem("totals $0 or less, which QuickBooks can't import"));
      continue;
    }
    if (rows.length > MAX_ROWS_PER_FILE) {
      skipped.push(problem(`has ${rows.length} lines, more than QuickBooks allows in one file`));
      continue;
    }
    total += invoiceTotal;
    tax += Number(p.taxAmount) || 0;
    invoices.push(rows);
  }

  // Pack whole invoices into files: never split one invoice across files.
  const files: Csv[] = [];
  let current: Csv = [];
  let count = 0;
  for (const rows of invoices) {
    if (
      current.length &&
      (count >= MAX_INVOICES_PER_FILE || current.length + rows.length > MAX_ROWS_PER_FILE)
    ) {
      files.push([INVOICE_HEADER, ...current]);
      current = [];
      count = 0;
    }
    current.push(...rows);
    count++;
  }
  if (current.length) files.push([INVOICE_HEADER, ...current]);

  return {
    files,
    invoiceCount: invoices.length,
    rowCount: files.reduce((n, f) => n + f.length - 1, 0),
    total: round2(total),
    tax: round2(tax),
    skipped,
    warnings,
  };
}

// RFC 4180 CSV: quote fields with commas, quotes or line breaks.
export function toCsv(rows: Csv): string {
  return (
    rows
      .map((r) =>
        r
          .map((v) => {
            const s = String(v ?? "");
            return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
          })
          .join(",")
      )
      .join("\r\n") + "\r\n"
  );
}
